import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

/**
 * useActiveTenantStore — pinia store qui porte la sélection de tenant
 * actif du super_admin (filtre cross-tenant côté UI).
 *
 * Contexte :
 *   - Un super_admin a accès à toutes les ressources tous tenants confondus.
 *     Sans filtre, les vues admin (api-keys, providers, users) retournent
 *     du contenu de tous les tenants mélangé — peu utilisable.
 *   - Le backend supporte déjà `?tenant_id=xxx` sur les endpoints admin
 *     (cf. handlers/controlplane/{apikeys,providers,users}.go). Quand le
 *     query param est fourni, super_admin filtre / agit sur ce tenant.
 *   - Le store maintient `activeTenantId` (null = tous tenants ou bien
 *     "mon tenant" si non-super_admin). Les adapters HTTP lisent ce
 *     store pour injecter le query param.
 *
 * Pour les rôles autres que super_admin, ce store est inerte : leur
 * tenant est forcé côté backend, le query param est ignoré.
 *
 * Persistance :
 *   - localStorage `p-active-tenant` pour survivre au reload (UX :
 *     un super_admin qui travaille sur le tenant X retrouve ce contexte
 *     au prochain login).
 *   - Reset complet via `clear()` au logout.
 */

const STORAGE_KEY = 'p-active-tenant'

export const useActiveTenantStore = defineStore('active-tenant', () => {
    const activeTenantId = ref<string | null>(loadFromStorage())

    /** true quand un tenant override est actif (super_admin a sélectionné un tenant). */
    const isOverridden = computed(() => activeTenantId.value !== null)

    /**
     * setActive : sélectionne un tenant comme contexte actif.
     * Doit être appelé UNIQUEMENT pour super_admin (l'UI gate l'accès au
     * sélecteur ; ce store ne fait pas la vérification RBAC).
     */
    function setActive(tenantId: string): void {
        activeTenantId.value = tenantId
        persist(tenantId)
    }

    /**
     * clearActive : revient à "tous tenants" (pas de filtre).
     */
    function clearActive(): void {
        activeTenantId.value = null
        persist(null)
    }

    /**
     * reset : appelé au logout — clear le store ET le localStorage.
     */
    function reset(): void {
        activeTenantId.value = null
        if (typeof window !== 'undefined') {
            window.localStorage.removeItem(STORAGE_KEY)
        }
    }

    return {
        activeTenantId,
        isOverridden,
        setActive,
        clearActive,
        reset,
    }
})

function loadFromStorage(): string | null {
    if (typeof window === 'undefined') return null
    const v = window.localStorage.getItem(STORAGE_KEY)
    return v && v.length > 0 ? v : null
}

function persist(value: string | null): void {
    if (typeof window === 'undefined') return
    if (value === null) {
        window.localStorage.removeItem(STORAGE_KEY)
    } else {
        window.localStorage.setItem(STORAGE_KEY, value)
    }
}

export type ActiveTenantStore = ReturnType<typeof useActiveTenantStore>
